import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageSEO, PageFAQSchema } from '../../components/seo/SchemaMarkup';
import {
  API, CompactRow, EmailCapture, Eyebrow, FaqBlock, LocatorLoading, StatBar,
  localBusinessList, useJsonLd, useLocatorPageView,
} from './locatorShared';
import { BiminiLocatorFeature } from '../../components/partners/BiminiPartnerFeature';

export default function LocatorTherapyHub() {
  const { therapy } = useParams();
  useLocatorPageView(therapy);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    setNotFound(false);
    fetch(`${API}/api/locator/therapy/${therapy}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setNotFound(true));
  }, [therapy]);

  useJsonLd(
    'locator-therapy-itemlist',
    data ? localBusinessList(data.groups.flatMap((g) => g.facilities), `/locator/therapy/${therapy}`, `${data.therapy.name} Near You`) : null,
  );

  if (notFound) {
    return (
      <div className="min-h-[60vh] bg-black flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-white text-3xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Unknown Therapy</h1>
          <Link to="/locator" className="text-[#C7FA3E] text-sm underline underline-offset-2">Browse the recovery locator</Link>
        </div>
      </div>
    );
  }
  if (!data) return <LocatorLoading />;

  const t = data.therapy;
  return (
    <article className="bg-black min-h-screen px-6 pt-28 pb-16" data-testid={`locator-therapy-${therapy}`}>
      <PageSEO
        title={`${t.name} Near You — ${t.count} Vetted Facilities Across the US | NinjAthlete`}
        description={`Find ${t.name.toLowerCase()} near you. ${t.count} vetted facilities across ${data.groups.length} regions with contact details and booking links.`}
        path={`/locator/therapy/${therapy}`}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Recovery Locator', url: '/locator' }, { name: t.name, url: `/locator/therapy/${therapy}` }]}
      />
      <PageFAQSchema faqs={data.faqs} pageName={`${t.name} Near You`} pagePath={`/locator/therapy/${therapy}`} scriptId="locator-therapy-faq" />
      <div className="max-w-5xl mx-auto">
        <Eyebrow>National Therapy Guide · Updated August 2026</Eyebrow>
        <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="locator-therapy-h1">
          {t.name} Near You
        </h1>
        <p className="text-zinc-400 text-base leading-relaxed mt-4 max-w-2xl">
          {t.blurb} <strong className="text-white">{t.count} vetted facilities</strong> across the US offer it — grouped by region below.
          {t.pillar && <> New to it? <a href={t.pillar} className="text-[#C7FA3E] hover:underline">Start with our guide</a>.</>}
        </p>

        <StatBar
          testPrefix={`therapy-${therapy}`}
          stats={[
            [`${t.count}`, 'Facilities nationwide'],
            [`${data.groups.filter((g) => g.is_metro).length}`, 'Metro areas'],
            [`${data.metro_pages.length}`, 'City guides'],
            ['Free', 'To claim a listing'],
          ]}
        />

        {data.metro_pages.length > 0 && (
          <div className="flex flex-wrap gap-2 my-6" data-testid={`therapy-${therapy}-city-chips`}>
            {data.metro_pages.map((p) => (
              <Link key={p.url} to={p.url} className="px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#C7FA3E] hover:text-[#C7FA3E] text-xs uppercase tracking-wider transition-colors">
                {p.name} guide
              </Link>
            ))}
          </div>
        )}

        <EmailCapture metro={`therapy-${therapy}`} metroName={null} testPrefix={`therapy-${therapy}`} />

        {data.groups.map((g) => (
          <section key={g.key} className="mt-8" data-testid={`therapy-group-${g.key}`}>
            <h2 className="text-white text-2xl mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              {g.label} ({g.facilities.length})
              {g.is_metro && <Link to={`/locator/${g.key}`} className="ml-3 text-[#C7FA3E] text-xs uppercase tracking-wider hover:underline align-middle" style={{ fontFamily: 'Oswald, sans-serif' }}>Full directory →</Link>}
            </h2>
            <div>
              {g.facilities.map((f) => <CompactRow key={f.slug} facility={f} page={`therapy-${therapy}`} />)}
            </div>
          </section>
        ))}

        <FaqBlock faqs={data.faqs} testPrefix={`therapy-${therapy}`} />

        <BiminiLocatorFeature therapy={therapy} />

        <section className="mt-12">
          <h2 className="text-white text-2xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>More Therapy Guides</h2>
          <div className="flex flex-wrap gap-2">
            {data.other_hubs.map((h) => (
              <Link key={h.slug} to={`/locator/therapy/${h.slug}`} className="px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-[#C7FA3E] hover:text-[#C7FA3E] text-xs uppercase tracking-wider transition-colors">
                {h.name}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </article>
  );
}
